import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useAppSelector } from '@/redux/hooks'
import { Editor } from '@toast-ui/react-editor'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  fieldCheck,
  GolfBizNm,
  PromotionData,
  PromoType,
  thumbnailSizeInfo,
  ThumbnailType,
} from '../promotionItem'
import {
  buttonName,
  confirmMessages,
  errorMessages,
} from '@/pages/board/boardItem'
import './promotion.css'
import { Button, Select } from '@/components'
import codeSyntaxHighlight from '@toast-ui/editor-plugin-code-syntax-highlight'
import colorSyntax from '@toast-ui/editor-plugin-color-syntax'
import FileUpload from './fileUpload'
import { FileUploadResponse } from '@/pages/blog/blogItem'
import { useResponsive } from '@/hooks/useResponsive'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

type PromotionEditorProps = {
  nameList: GolfBizNm[]
}

type FormData = {
  nttSj: string
  nttCn: string
  prmtType: string
  prmtStartDate: string
  prmtEndDate: string
  thumbnail: string
}

export const PromotionEditor = ({ nameList }: PromotionEditorProps) => {
  const axios = useAxios()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()
  const editorRef = useRef<Editor>(null)

  // 통합 파일 상태 관리
  const [fileSeqList, setFileSeqList] = useState<number[]>([])
  const [fileNameList, setFileNameList] = useState<string[]>([])
  const [fileSourceList, setFileSourceList] = useState<string[]>([])
  const [deleteSeqList, setDeleteSeqList] = useState<number[]>([])
  const [deleteNameList, setDeleteNameList] = useState<string[]>([])
  const [resetUploadTrigger, setResetUploadTrigger] = useState(false)

  // 에디터 내 이미지와 파일 seq 매핑을 위한 상태
  const [editorImageMap, setEditorImageMap] = useState<
    Map<string, { seq: number; name: string }>
  >(new Map())
  // 최종 저장용 HTML state (사이즈 정보 포함)
  const [finalNttCn, setFinalNttCn] = useState<string>('')
  // 이미지 정보를 저장할 ref (URL -> {width, height})
  const imageInfoMapRef = useRef<
    Map<string, { width: number; height: number }>
  >(new Map())
  // 파일 선택 상태를 관리하는 state
  const [hasUnuploadedFiles, setHasUnuploadedFiles] = useState(false)
  const [isUnlimited, setIsUnlimited] = useState(false)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const [formData, setFormData] = useState<PromotionData>({
    cmpnyCd: '001',
    cstId: user?.cstId || '',
    nttSn: 0,
    regId: user?.cstId || '',
    regDate: '',
    updId: user?.cstId || '',
    updDate: '',
    prmtType: '01',
    bizCd: '',
    nttSj: '',
    nttCn: '',
    prmtStartDate: '',
    prmtEndDate: '',
    prmtUrl: '',
    page: 1,
    srchOpt: '',
    srchStr: '',
    thumbnail: 'custom',
    thumbnailSeq: null,
    fileSeqList: fileSeqList,
    fileNameList: fileNameList,
    fileSourceList: fileSourceList, // 추가
    deleteSeqList: deleteSeqList,
  })

  // 구분에서 전체 빼고 만들기
  const filteredPromoTypes = PromoType.filter((type) => type.value !== '00')

  // useEffect(() => {
  //   console.log('formData 변경', formData)
  // }, [formData])

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  // 에디터 이미지 삭제 감지를 위한 훅 등록
  useEffect(() => {
    if (editorRef.current) {
      const editorInstance = editorRef.current.getInstance()

      // 에디터에서 키 이벤트 감지 (Delete, Backspace)
      editorInstance.addHook('keydown', (keyEvent: KeyboardEvent) => {
        if (keyEvent.key === 'Delete' || keyEvent.key === 'Backspace') {
          setTimeout(() => {
            checkDeletedImagesInEditor()
          }, 100)
        }
      })

      // 에디터 내용이 직접 변경되었을 때도 체크
      editorInstance.addHook('change', () => {
        setTimeout(() => {
          checkDeletedImagesInEditor()
        }, 100)
      })
    }
  }, [editorImageMap, fileSeqList])

  // useEffect(() => {
  //   // 시작날짜 오늘날짜로 셋팅
  //   if (!formData.prmtStartDate) {
  //     const today = new Date()
  //     const yyyy = today.getFullYear()
  //     const mm = String(today.getMonth() + 1).padStart(2, '0')
  //     const dd = String(today.getDate()).padStart(2, '0')
  //     const todayStr = `${yyyy}-${mm}-${dd}`

  //     setFormData((prev) => ({
  //       ...prev,
  //       prmtStartDate: todayStr,
  //     }))
  //   }
  // }, [])

  // 골프장리스트 첫번째 값 셋팅해주기
  useEffect(() => {
    if (formData.prmtType === '01' && nameList.length > 0 && !formData.bizCd) {
      setFormData((prev) => ({
        ...prev,
        //bizCd: nameList[0].bizCd,
      }))
    }
  }, [formData.prmtType, nameList])

  // formData 업데이트에 파일 관련 상태들 포함
  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      fileSeqList: fileSeqList,
      fileNameList: fileNameList,
      fileSourceList: fileSourceList,
      deleteSeqList: deleteSeqList,
    }))
  }, [fileSeqList, fileNameList, fileSourceList, deleteSeqList])

  // 에디터 이미지 업로드 훅
  // useEffect(() => {
  //   if (editorRef.current) {
  //     editorRef.current
  //       .getInstance()
  //       .addHook(
  //         'addImageBlobHook',
  //         async (
  //           blob: Blob,
  //           callback: (url: string, altText: string) => void,
  //         ) => {
  //           const maxFileSize = 10 * 1024 * 1024 // 10MB

  //           if (blob.size > maxFileSize) {
  //             showAlert({
  //               title: '10MB 이상의 파일은 업로드 할 수 없어요.',
  //               message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
  //             })
  //             return
  //           }

  //           const { fileSeq, fileUrl } = await uploadImageToServer(blob)

  //           if (fileUrl) {
  //             // 먼저 이미지 URL을 ToastUI에 삽입하도록 callback을 사용
  //             callback(fileUrl, '')

  //             const editorInstance = editorRef.current?.getInstance()
  //             const currentHTML = editorInstance?.getHTML()

  //             if (!currentHTML) return

  //             let fixedHTML = currentHTML
  //               // 텍스트 + 이미지가 한 문단에 같이 있는 경우 분리
  //               .replace(
  //                 /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
  //                 (_, textBefore, imgTag, textAfter) => {
  //                   const parts = []
  //                   if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
  //                   parts.push(`<p>${imgTag}</p>`)
  //                   if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
  //                   return parts.join('')
  //                 },
  //               )

  //             // 마지막이 <img><br> 로 끝날 경우 개행용 <p> 추가
  //             if (fixedHTML.match(/<p><img[^>]+><br><\/p>$/)) {
  //               fixedHTML += '<p></p>'
  //             }

  //             editorInstance?.setHTML(fixedHTML)
  //           }
  //           return false
  //         },
  //       )
  //   }
  // }, [])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current
        .getInstance()
        .addHook(
          'addImageBlobHook',
          async (
            blob: Blob,
            callback: (url: string, altText: string) => void,
          ) => {
            const maxFileSize = 10 * 1024 * 1024 // 10MB

            if (blob.size > maxFileSize) {
              showAlert({
                title: '10MB 이상의 파일은 업로드 할 수 없어요.',
                message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
              })
              return
            }

            // 원본 이미지 크기 확인
            const checkImageSize = new Promise<{
              width: number
              height: number
            }>((resolve) => {
              const img = new Image()
              img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
              }
              img.src = URL.createObjectURL(blob)
            })

            const originalSize = await checkImageSize
            console.log('원본 이미지 크기:', originalSize)

            const { fileSeq, fileUrl } = await uploadImageToServer(blob)

            if (fileUrl) {
              console.log('[DEBUG] 이미지 URL 획득:', fileUrl)

              // 이미지 정보를 ref에 저장
              imageInfoMapRef.current.set(fileUrl, {
                width: originalSize.width,
                height: originalSize.height,
              })

              // 1. 에디터 화면에는 일반 이미지로 표시 (사용자가 볼 수 있도록)
              callback(fileUrl, '')

              // 2. 짧은 지연 후 finalNttCn state 업데이트 (모든 이미지에 사이즈 정보 포함)
              setTimeout(() => {
                const editorInstance = editorRef.current?.getInstance()
                if (!editorInstance) return

                const currentHTML = editorInstance.getHTML()
                console.log('[DEBUG] 현재 에디터 HTML:', currentHTML)

                // 모든 이미지에 스타일을 적용한 HTML 생성
                const styledHTML = applyImageStyles(currentHTML)

                console.log('[DEBUG] 최종 HTML 업데이트:', styledHTML)
                setFinalNttCn(styledHTML)
              }, 100)
            }
            return false
          },
        )
    }
  }, [])

  const applyImageStyles = (html: string) => {
    let styledHTML = html

    // 텍스트 + 이미지가 한 문단에 같이 있는 경우 분리
    styledHTML = styledHTML.replace(
      /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
      (_, textBefore, imgTag, textAfter) => {
        const parts = []
        if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
        parts.push(`<p>${imgTag}</p>`)
        if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
        return parts.join('')
      },
    )

    // 마지막이 <img><br> 로 끝날 경우 개행용 <p> 추가
    if (styledHTML.match(/<p><img[^>]+><br><\/p>$/)) {
      styledHTML += '<p></p>'
    }

    // 모든 이미지에 스타일 적용
    imageInfoMapRef.current.forEach(({ width, height }, url) => {
      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

      // 이미 style이 있는 경우와 없는 경우 모두 처리
      const imgRegex = new RegExp(
        `<img([^>]*?)src="${escapedUrl}"(?:[^>]*?style="[^"]*")?([^>]*?)>`,
        'g',
      )

      styledHTML = styledHTML.replace(
        imgRegex,
        `<img src="${url}" style="width: ${width}px; height: ${height}px;" />`,
      )
    })

    return styledHTML
  }

  const isValidUrl = (url: string) => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // http:// or https:// (optional)
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*))\\.)+[a-z]{2,}' + // domain name
        '(\\:\\d+)?(\\/[-a-z\\d%@_.~+&:]*)*' + // port and path
        '(\\?[;&a-z\\d%@_.,~+&:=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', // fragment
      'i',
    )
    return !!urlPattern.test(url)
  }

  const handleChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement

    // 체크박스 처리 (종료일 미지정)
    if (type === 'checkbox' && name === 'unlimited' && value === 'unlimited') {
      setIsUnlimited(checked)

      if (checked) {
        setFormData((prev) => ({ ...prev, prmtEndDate: '2099-12-31' }))
        showAlert({
          title: '종료일을 미지정으로 선택했어요.',
          message: '미지정시 게시물이 계속 노출돼요.',
        })
      } else {
        setFormData((prev) => ({ ...prev, prmtEndDate: '' }))
      }
      return
    }

    if (name === 'prmtStartDate') {
      if (
        formData.prmtEndDate &&
        new Date(value) > new Date(formData.prmtEndDate)
      ) {
        showAlert({
          type: 'error',
          title: '시작일은 종료일 이전이어야 해요.',
        })
        return
      }
    }

    if (name === 'prmtEndDate') {
      if (
        formData.prmtStartDate &&
        new Date(value) < new Date(formData.prmtStartDate)
      ) {
        showAlert({
          type: 'error',
          title: '종료일은 시작일 이후여야 해요.',
        })
        return
      }

      if (isUnlimited) {
        setIsUnlimited(false)
      }
    }

    if (name === 'thumbnail') {
      const selectedValue = value
      if (selectedValue !== 'custom') {
        setResetUploadTrigger(true)
      } else {
        setResetUploadTrigger(false)
      }
      setFormData((prev) => ({ ...prev, thumbnail: selectedValue }))
    }

    setFormData((prevData) => {
      // prmtType이 골프장이 아닌 값을 선택했을 경우 bizCd 초기화
      if (name === 'prmtType' && value !== '01') {
        return {
          ...prevData,
          [name]: value,
          bizCd: '',
        }
      }
      return {
        ...prevData,
        [name]: value,
      }
    })
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'prmtUrl' && !isValidUrl(value)) {
      showAlert({
        title: '올바른 URL 형식을 입력해주세요.',
      })
    }
  }

  const handleEditorChange = () => {
    const editorNttCn = editorRef.current?.getInstance()
    if (editorNttCn) {
      const nttCn = editorNttCn.getHTML()
      setFormData((prev) => ({ ...prev, nttCn }))

      // 에디터 내용이 변경될 때마다 삭제된 이미지 체크
      setTimeout(() => {
        checkDeletedImagesInEditor()
      }, 100)
    }
  }

  // 에디터 이미지 업로드 서버 함수 (feedbackEditor와 동일한 방식)
  const uploadImageToServer = async (
    blob: Blob,
  ): Promise<{
    fileUrl: string
    fileSeq: number | null
    fileSource: string
  }> => {
    const formData = new FormData()
    formData.append('file', blob)

    try {
      const response = await axios.post<{ data: FileUploadResponse }>(
        '/promotion/fileUpload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )

      const fileUrl =
        process.env.REACT_APP_BASE_URL + '/upload/' + response.data.data.fileNm
      const fileSeq = response.data.data.fileSeq
      const fileName = response.data.data.fileNm
      const fileSource = 'EDITOR'

      // 에디터 이미지 매핑 정보 저장 (URL을 키로 사용)
      setEditorImageMap((prev) => {
        const newMap = new Map(prev)
        newMap.set(fileUrl, { seq: fileSeq, name: fileName })
        return newMap
      })

      setFileSeqList((prev) => [...prev, fileSeq])
      setFileNameList((prev) => [...prev, fileName])
      setFileSourceList((prev) => [...prev, fileSource])

      return { fileUrl, fileSeq, fileSource }
    } catch (error) {
      showAlert({
        title: '이미지 업로드 실패',
        message: error,
        btnName: '확인',
      })
      return { fileUrl: '', fileSeq: null, fileSource: '' }
    }
  }

  // 에디터 내용 변경 감지하여 삭제된 이미지 찾기
  const checkDeletedImagesInEditor = () => {
    if (!editorRef.current) return

    const currentHTML = editorRef.current.getInstance().getHTML()
    const currentImageUrls = new Set<string>()

    // 현재 에디터에 있는 이미지 URL들 추출
    const imgRegex = /<img[^>]+src="([^"]+)"/g
    let match
    while ((match = imgRegex.exec(currentHTML)) !== null) {
      currentImageUrls.add(match[1])
    }

    // 매핑된 이미지 중에서 현재 에디터에 없는 것들 찾기
    const deletedImages: { seq: number; name: string }[] = []

    editorImageMap.forEach((imageInfo, imageUrl) => {
      if (!currentImageUrls.has(imageUrl)) {
        deletedImages.push(imageInfo)
      }
    })

    if (deletedImages.length > 0) {
      // 삭제된 이미지들을 삭제 목록에 추가
      const deletedSeqs = deletedImages.map((img) => img.seq)
      const deletedNames = deletedImages.map((img) => img.name)

      setDeleteSeqList((prev) => {
        const newList = [...prev, ...deletedSeqs]
        return Array.from(new Set(newList))
      })

      setDeleteNameList((prev) => {
        const newList = [...prev, ...deletedNames]
        return Array.from(new Set(newList))
      })

      // fileSeqList와 fileNameList에서도 제거
      setFileSeqList((prev) => prev.filter((seq) => !deletedSeqs.includes(seq)))
      setFileNameList((prev) =>
        prev.filter((name) => !deletedNames.includes(name)),
      )
      setFileSourceList((prev) => {
        return prev.filter((source, index) => {
          const currentSeq = fileSeqList[index]
          return !deletedSeqs.includes(currentSeq)
        })
      })

      // 매핑에서도 삭제된 이미지들 제거
      setEditorImageMap((prev) => {
        const newMap = new Map(prev)
        deletedImages.forEach((img) => {
          // URL을 키로 찾아서 삭제
          for (const [url, info] of newMap.entries()) {
            if (info.seq === img.seq) {
              newMap.delete(url)
              break
            }
          }
        })
        return newMap
      })
    }
  }

  const handleModalAction = (type: 'insert' | 'cancel') => {
    if (type === 'insert') {
      const requiredFields: { field: keyof FormData; message: string }[] = [
        { field: 'nttSj', message: fieldCheck.nttSj },
        { field: 'nttCn', message: fieldCheck.nttCn },
        { field: 'prmtType', message: fieldCheck.prmtType },
        { field: 'prmtStartDate', message: fieldCheck.prmtStartDate },
        { field: 'prmtEndDate', message: fieldCheck.prmtEndDate },
        { field: 'thumbnail', message: fieldCheck.thumbnail },
      ]

      for (const { field, message } of requiredFields) {
        const value = formData[field]

        if (field === 'nttCn') {
          const strippedText = value
            ?.replace(/<(?!img\s*[^>]*>)[^>]+>/g, ' ')
            .trim()

          if (
            strippedText === '' ||
            strippedText?.replace(/\s+/g, '').length === 0
          ) {
            showAlert({
              type: 'warning',
              title: message,
            })
            return
          }
        } else if (typeof value === 'string' && !value.trim()) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }

        if (field === 'thumbnail') {
          const type = formData.thumbnail
          // custom 선택 시 썸네일 파일이 업로드되었는지 확인
          if (type === 'custom') {
            const thumbnailFiles = fileSeqList.filter(
              (seq, index) => fileSourceList[index] === 'UPLOAD',
            )
            if (thumbnailFiles.length === 0) {
              showAlert({
                type: 'warning',
                title: message,
              })
              return
            }
          }
        }

        if (!formData[field]) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }
      }

      // 업로드되지 않은 파일이 있는지 확인
      if (hasUnuploadedFiles) {
        showAlert({
          type: 'warning',
          title: '선택된 파일을 먼저 업로드해주세요.',
        })
        return
      }

      showConfirm({
        title: confirmMessages.insert,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          insertPromo()
        },
      })
    }

    if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(`/promotion`, { state: location.state })
          }, 100)
        },
      })
    }
  }

  const insertPromo = async () => {
    // 삭제되지 않은 썸네일 파일 찾기
    const finalThumbnailFiles = fileSeqList.filter(
      (seq, index) =>
        fileSourceList[index] === 'UPLOAD' && !deleteSeqList.includes(seq),
    )

    const finalThumbnailSeq =
      finalThumbnailFiles.length > 0 ? finalThumbnailFiles[0] : null

    const submitFormData = {
      ...formData,
      thumbnailSeq: finalThumbnailSeq,
      nttCn: finalNttCn || formData.nttCn,
    }

    try {
      await axios.post(`/promotion/insert`, submitFormData)

      setTimeout(() => {
        navigate(`/promotion`, { state: location.state })
      }, 300)
    } catch (err: any) {
      showAlert({
        type: 'error',
        title: errorMessages.insert,
        message: err.response?.data?.message,
        btnName: buttonName.confirm,
      })
    }
  }

  // 파일 업로드 완료 핸들러 (feedbackEditor와 동일한 방식)
  const handleUploadedFile = (
    uploadSeqs: number[],
    uploadNames: string[],
    uploadSources: string[],
  ) => {
    setFileSeqList((prev) => [...prev, ...uploadSeqs])
    setFileNameList((prev) => [...prev, ...uploadNames])
    setFileSourceList((prev) => [...prev, ...uploadSources])
  }

  // 파일 삭제 핸들러 (feedbackEditor와 동일한 방식)
  const handleDeletedFile = (fileSeqs: number[], fileNames: string[]) => {
    // 삭제 리스트 업데이트
    setDeleteSeqList(fileSeqs)
    setDeleteNameList(fileNames)

    // fileSeqList에서 삭제된 파일들 제거
    setFileSeqList((prev) => prev.filter((seq) => !fileSeqs.includes(seq)))

    // fileNameList에서도 삭제된 파일명들 제거
    setFileNameList((prev) => prev.filter((name) => !fileNames.includes(name)))

    // fileSourceList에서도 해당 인덱스의 소스들 제거
    setFileSourceList((prev) => {
      const newSources = prev.filter((source, index) => {
        const currentSeq = fileSeqList[index]
        return !fileSeqs.includes(currentSeq)
      })
      return newSources
    })
  }

  const handleFileSelectionChange = (hasSelectedFiles: boolean) => {
    setHasUnuploadedFiles(hasSelectedFiles)
  }

  return (
    <>
      {!isMobile ? (
        <>
          <div className="edit-container">
            <div className="edit-group">
              <label className="required-label">구분</label>
              <div className="radio-group">
                {filteredPromoTypes.map((type) => (
                  <label key={type.value}>
                    <input
                      type="radio"
                      name="prmtType"
                      value={type.value}
                      checked={formData.prmtType === type.value}
                      onChange={handleChange}
                    />
                    {type.label}

                    {type.value === '01' && formData.prmtType === '01' && (
                      <Select
                        id="bizCd"
                        name="bizCd"
                        width="100px"
                        className={`golf-select ${formData.prmtType === '01' ? 'visible' : 'hidden'}`}
                        value={formData.bizCd}
                        onChange={handleChange}
                        style={{ marginLeft: '8px' }}
                      >
                        <option value="">선택</option>
                        {nameList.map((option, index) => (
                          <option
                            key={`${option.areaCd}-${index}`}
                            value={option.bizCd}
                          >
                            {option.bizNm}
                          </option>
                        ))}
                      </Select>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="edit-group">
              <label className="required-label">프로모션 기간</label>
              <div className="date-group">
                <input
                  type="date"
                  name="prmtStartDate"
                  value={formData.prmtStartDate}
                  onChange={handleChange}
                />
                ~
                {isUnlimited ? (
                  <div className="unlimited">
                    종료일을 미지정으로 선택했어요.
                  </div>
                ) : (
                  <input
                    type="date"
                    name="prmtEndDate"
                    value={formData.prmtEndDate}
                    onChange={handleChange}
                  />
                )}
                <div className="radio-group">
                  <label>
                    <input
                      type="checkbox"
                      name="unlimited"
                      value="unlimited"
                      checked={isUnlimited}
                      onChange={handleChange}
                    />
                    종료일 미지정
                  </label>
                </div>
              </div>
            </div>

            <div className="edit-group">
              <label className="required-label">프로모션 명</label>
              <div
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  gap: '5px',
                }}
              >
                <input
                  type="text"
                  name="nttSj"
                  maxLength={255}
                  value={formData.nttSj}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="edit-group">
              <label>프로모션 링크</label>
              <input
                type="text"
                name="prmtUrl"
                maxLength={255}
                value={formData.prmtUrl}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="edit-group">
              <label className="required-label">
                대표 이미지
                <span className="hint-text">{thumbnailSizeInfo}</span>
              </label>
              <div className="radio-group">
                {ThumbnailType.map((type) => (
                  <label key={type.value}>
                    <input
                      type="radio"
                      name="thumbnail"
                      value={type.value}
                      checked={formData.thumbnail === type.value}
                      onChange={handleChange}
                    />
                    {type.label}
                    {/* 'custom' 타입일 때만 FileUpload 컴포넌트 렌더링 */}
                    {type.value === 'custom' &&
                      formData.thumbnail === 'custom' && (
                        <div className="upload-wrapper">
                          <FileUpload
                            onDeletedFile={handleDeletedFile}
                            onUploadedFile={handleUploadedFile}
                            onFileSelectionChange={handleFileSelectionChange}
                            resetUploadTrigger={resetUploadTrigger}
                          />
                        </div>
                      )}
                  </label>
                ))}
              </div>
            </div>
            <Editor
              ref={editorRef}
              useCommandShortcut={true}
              placeholder="내용을 입력해 주세요!"
              previewStyle="vertical"
              height="500px"
              initialEditType="wysiwyg"
              onChange={handleEditorChange}
              language="ko-KR"
              hideModeSwitch={true}
              plugins={[colorSyntax, codeSyntaxHighlight]}
            />
          </div>

          <div className="promo-button">
            <Button color="neverbe" onClick={() => handleModalAction('insert')}>
              저장
            </Button>
            <Button color="neverbe" onClick={() => handleModalAction('cancel')}>
              취소
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="m-edit-container">
            <div className="m-edit-header">
              <h1 className="m-edit-title">정보</h1>
              <button
                className="blog-back-btn"
                onClick={() => handleModalAction('cancel')}
              >
                ← 뒤로
              </button>
            </div>
            <div className="m-edit-form">
              <div className="m-edit-group">
                <label className="m-label m-required-label">구분</label>
                <div className="radio-group">
                  {PromoType.filter((type) => type.value !== '00').map(
                    (type) => (
                      <label key={type.value}>
                        <input
                          type="radio"
                          name="prmtType"
                          value={type.value}
                          checked={formData.prmtType === type.value} // formData.prmtType과 비교하여 선택 상태 결정
                          onChange={handleChange}
                        />
                        {type.label}
                      </label>
                    ),
                  )}
                  <Select
                    id="bizCd"
                    name="bizCd"
                    width="110px"
                    className={`golf-select ${formData.prmtType === '01' ? 'visible' : 'hidden'}`}
                    value={formData.bizCd}
                    onChange={handleChange}
                  >
                    {nameList.map((option, index) => (
                      <option
                        key={`${option.areaCd}-${index}`}
                        value={option.bizCd}
                      >
                        {option.bizNm}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="m-edit-group">
                <div className="radio-group">
                  <label className="m-label m-required-label">
                    프로모션 기간
                  </label>
                </div>
                <div className="date-group">
                  <input
                    type="date"
                    name="prmtStartDate"
                    style={{ width: '150px' }}
                    value={formData?.prmtStartDate}
                    onChange={handleChange}
                  />
                  ~
                  {isUnlimited ? (
                    <div className="unlimited" style={{ height: '40px' }}>
                      종료일 미지정
                    </div>
                  ) : (
                    <input
                      type="date"
                      name="prmtEndDate"
                      style={{ width: '150px' }}
                      value={formData.prmtEndDate}
                      onChange={handleChange}
                    />
                  )}
                </div>
                <div className="radio-option" style={{ marginTop: '5px' }}>
                  <input
                    type="checkbox"
                    name="unlimited"
                    value="unlimited"
                    checked={isUnlimited}
                    onChange={handleChange}
                  />
                  <span>종료일 미지정</span>
                </div>
              </div>

              <div className="m-edit-group">
                <label className="m-label m-required-label">프로모션 명</label>
                <div style={{ marginTop: '10px' }} />
                <input
                  type="text"
                  name="nttSj"
                  maxLength={255}
                  value={formData.nttSj}
                  onChange={handleChange}
                />
              </div>

              <div className="m-edit-group">
                <label className="m-label">프로모션 링크</label>
                <div style={{ marginTop: '10px' }} />
                <input
                  type="text"
                  name="prmtUrl"
                  maxLength={255}
                  value={formData.prmtUrl}
                  onChange={handleChange}
                />
              </div>
              <div className="m-edit-group" style={{ marginBottom: '0px' }}>
                <label className="m-label m-required-label">
                  대표 이미지
                  <span className="m-hint-text">{thumbnailSizeInfo}</span>
                </label>
                <div className="m-radio-group">
                  {ThumbnailType.map((type) => (
                    <label className="m-radio-label" key={type.value}>
                      <div className="m-radio-content">
                        <div className="m-radio-text-line">
                          <input
                            className="m-radio-input"
                            type="radio"
                            name="thumbnail"
                            value={type.value}
                            checked={formData.thumbnail === type.value}
                            onChange={handleChange}
                          />
                          <span>{type.label}</span>
                          {/* 파일명을 라벨 바로 옆에 표시 */}
                          {/* {type.value === 'custom' &&
                            fileOrgNm &&
                            !resetUploadTrigger && (
                              <div className="m-file-name" key={thumbnailSeq}>
                                <PreviewIcon />
                                <span className="m-file-name-text">
                                  {fileOrgNm}
                                </span>
                              </div>
                            )} */}
                        </div>

                        {/* FileUpload 컴포넌트를 라벨 아래에 배치 */}
                        {type.value === 'custom' &&
                          formData.thumbnail === 'custom' && (
                            <div className="upload-wrapper">
                              <FileUpload
                                onDeletedFile={handleDeletedFile}
                                onUploadedFile={handleUploadedFile}
                                onFileSelectionChange={
                                  handleFileSelectionChange
                                }
                                resetUploadTrigger={resetUploadTrigger}
                              />
                            </div>
                          )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <Editor
                ref={editorRef}
                useCommandShortcut={true}
                placeholder="내용을 입력해 주세요!"
                previewStyle="vertical"
                height="500px"
                initialEditType="wysiwyg"
                onChange={handleEditorChange}
                language="ko-KR"
                hideModeSwitch={true}
                plugins={[colorSyntax, codeSyntaxHighlight]}
              />
              <div className="mobile-post-actions">
                <button
                  type="button"
                  className="mobile-action-btn primary"
                  onClick={() => handleModalAction('insert')}
                >
                  저장
                </button>
                <button
                  type="button"
                  className="mobile-action-btn user"
                  onClick={() => handleModalAction('cancel')}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
